/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { Component, Types } from 'ecsy';

export class RemotePlayerComponent extends Component {}

RemotePlayerComponent.schema = {
	id: { type: Types.String },
	name: { type: Types.String },
	country: { type: Types.String },
	sessionId: { type: Types.String },
	// THREE.Group for the player's body representation
	model: { type: Types.Ref },
	// THREE.Group for the player's head representation
	head: { type: Types.Ref },

	// Transform data (from server)
	targetPos: { type: Types.Ref },
	targetRot: { type: Types.Ref },
	targetHeadPos: { type: Types.Ref },
	targetHeadRot: { type: Types.Ref },
};

export class MultiplayerStateComponent extends Component {}

MultiplayerStateComponent.schema = {
	client: { type: Types.Ref },
	room: { type: Types.Ref },
	isConnected: { type: Types.Boolean, default: false },
	localPlayerName: { type: Types.String },
	localPlayerCountry: { type: Types.String },
};

export class LeaderboardComponent extends Component {}

LeaderboardComponent.schema = {
	totalVisitors: { type: Types.Number, default: 0 },
	visitorCountries: { type: Types.Ref, default: {} }, // Map of country code -> count
};

/**
 * Identity + plot assignment for the persistent shared garden world.
 *
 * A single instance is created by SharedWorldSystem. When Firebase is not
 * configured, `configured` stays false and every other field is left at its
 * default — the shared world is dormant and the game runs single-player.
 */
export class SharedWorldComponent extends Component {}

SharedWorldComponent.schema = {
	// True once real Firebase credentials are present (see isFirebaseConfigured).
	configured: { type: Types.Boolean, default: false },
	// True once anonymous sign-in + plot allocation have completed.
	ready: { type: Types.Boolean, default: false },
	// Stable per-device anonymous auth uid.
	uid: { type: Types.String, default: '' },
	// This player's own plot (the only plot they may edit).
	plotId: { type: Types.String, default: '' },
	plotIndex: { type: Types.Number, default: -1 },
};

/**
 * Marks a plant that belongs to another player in the shared world. Foreign
 * plants are rendered from Firestore / live relays but are NOT tagged
 * SavableObject (so they never enter the local save) and cannot be harvested by
 * the viewer. These only exist while the shared world is live, so gameplay
 * systems that key off this component are inert in single-player.
 */
export class ForeignPlantComponent extends Component {}

ForeignPlantComponent.schema = {
	// Stable id shared across clients, used to remove the plant on a live relay.
	plantId: { type: Types.String, default: '' },
	// The plot (and therefore player) this plant belongs to.
	plotId: { type: Types.String, default: '' },
	ownerUid: { type: Types.String, default: '' },
};
